import ashpS4sConcentrations from './data/ashpS4sConcentrations.json';
import rtuFormulationsData from './data/rtuFormulations.json';

// Placeholder data for the Formulary Investigate page.
//
// TEMPORARY — every export mirrors the response shape of an endpoint that does
// not exist yet; see INVESTIGATE_DATA_CONTRACTS.md (the ### numbers match its
// sections). The seam is services/investigation.js. Delete this file once the
// endpoints are live.
//
// As in discoverFixtures.js, values derive from a hash of the medication name
// and the filter selection, so they are stable across reloads and every filter
// visibly moves them. #4 and #5 are the exception: they're real reference data
// (ASHP Standardize 4 Safety and an RTU catalog), not hashed placeholders —
// see data/README.md.

function hash(text) {
  let value = 0;
  for (let i = 0; i < text.length; i += 1) {
    value = (value * 31 + text.charCodeAt(i)) % 100000;
  }
  return value;
}

// Facility, population and date range can't SELECT rows in a fixture keyed by
// medication, so they shift the values instead: each combination gives a
// different but repeatable dataset. Sorted before joining so A-then-B and
// B-then-A agree; the date range contributes its span rather than its endpoints.
function seedFor(dataset, scope = {}) {
  const parts = [dataset, String(scope.medicationName || '').toLowerCase()];

  const facilities = [...(scope.facilities || [])].sort();
  if (facilities.length) parts.push(`f=${facilities.join('|')}`);

  const populations = [...(scope.patientPopulations || [])].sort();
  if (populations.length) parts.push(`p=${populations.join('|')}`);

  if (scope.startDate && scope.endDate) {
    const start = Date.parse(scope.startDate);
    const end = Date.parse(scope.endDate);
    if (!Number.isNaN(start) && !Number.isNaN(end)) {
      parts.push(`d=${Math.round((end - start) / 86400000)}`);
    }
  }

  return parts.join(':');
}

// ### 1 — A fixture can't know a drug's real formulations, so every medication
// draws from this shared catalog and its hash decides how many it uses. Units
// vary on purpose: the table and legend both run through the unit-preference
// formatter, so a mix exercises the conversion path.
const FORMULATION_CATALOG = [
  { concentration: 1, unit: 'mg/mL', volume: 50 },
  { concentration: 2, unit: 'mg/mL', volume: 100 },
  { concentration: 4, unit: 'mg/mL', volume: 250 },
  { concentration: 8, unit: 'mg/mL', volume: 100 },
  { concentration: 16, unit: 'mg/mL', volume: 250 },
  { concentration: 400, unit: 'mcg/mL', volume: 100 },
  { concentration: 100, unit: 'units/mL', volume: 50 },
];

const formulationString = ({ concentration, unit, volume }) => `${concentration} ${unit} in ${volume} mL`;

function formulationsFor(scope) {
  const seed = seedFor('formulations', scope);
  const count = 3 + (hash(seed) % 4);
  // So different medications don't all start at the same catalog entry.
  const offset = hash(`${seed}:offset`) % FORMULATION_CATALOG.length;

  return Array.from({ length: count }, (_, index) => {
    const entry = FORMULATION_CATALOG[(offset + index) % FORMULATION_CATALOG.length];
    return { ...entry, formulation: formulationString(entry) };
  });
}

// A formulation's row seed, and the utilization figure derived from it. Pulled
// out so `formulationsInUse` compares the floor against exactly the number
// buildFormulationRows will report, rather than a parallel derivation of it.
const rowSeed = (scope, formulation) => hash(`${seedFor('formulations', scope)}:${formulation}`);

const tableInfusions = (scope, formulation) => 20 + (rowSeed(scope, formulation) % 4800);

// `minInfusions` is the "in use" floor: a formulation whose utilization falls
// below it is not considered stocked, so it drops out entirely.
//
// Returns 1 for an absent or unparseable value: the catalog default of 5 is
// meant to be a near-no-op that only rules out formulations with almost no use.
function infusionFloor(scope = {}) {
  const minInfusions = Number.parseInt(scope.minInfusions, 10);
  return !Number.isNaN(minInfusions) && minInfusions > 1 ? minInfusions : 1;
}

// The formulations in use for `scope` — the floor's decision, made ONCE.
//
// Both ### 2 and ### 3 filter through this rather than each comparing the floor
// to its own numbers. That is the whole point: the table and the chart are two
// views of one filtered row set, so "is this formulation in use" has to have a
// single answer. Comparing the same floor against two independently-generated
// magnitudes gave unrelated answers — at some floors the chart plotted a series
// the table did not list at all, and vice versa.
//
// The utilization figure is the TABLE's `infusions`, because that is the number
// the user is reading when they drag the floor. The real endpoints have one
// underlying container count to compare against and so get this for free; the
// fixture has to be told.
function formulationsInUse(scope) {
  const floor = infusionFloor(scope);
  const entries = formulationsFor(scope);
  if (floor <= 1) return new Set(entries.map((entry) => entry.formulation));

  return new Set(
    entries.filter((entry) => tableInfusions(scope, entry.formulation) >= floor).map((entry) => entry.formulation)
  );
}

// ### 2 — GET /formulations. Field names are the endpoint's camelCase contract;
// the store's row shaper maps them to the table's shape. `wasteWeightedPct`
// spans 2-38% so the table exercises all three waste bands.
//
// Returns `{ formulations, summary }`. The server owns three things the client
// used to do for itself, all of them properties of the row set it is already
// scanning:
//
//   1. drop rows with no infusions in range (`infusions <= 0`)
//   2. sort by infusions descending, ties broken by formulation
//   3. aggregate the summary row (`buildFormulationSummary`)
//
// See INVESTIGATE_DATA_CONTRACTS.md #2 for why each belongs to the server.
export function buildFormulationRows(scope = {}) {
  const inUse = formulationsInUse(scope);

  return formulationsFor(scope)
    .filter((entry) => inUse.has(entry.formulation))
    .map((entry) => {
      const seed = rowSeed(scope, entry.formulation);

      // Tracks concentration rather than being independent, so the rate columns
      // can't contradict the concentration column.
      const minRate = Math.round((5 + (seed % 20)) * 10) / 10;

      return {
        formulation: entry.formulation,
        infusions: tableInfusions(scope, entry.formulation),
        wasteWeightedPct: Math.round((2 + (seed % 360) / 10) * 10) / 10,
        concentrationValue: entry.concentration,
        concentrationUnit: entry.unit,
        volumeValue: entry.volume,
        volumeUnit: 'mL',
        minRate,
        maxRate: minRate + Math.round((10 + (seed % 90)) * 10) / 10,
      };
    })
    .sort((a, b) => b.infusions - a.infusions || a.formulation.localeCompare(b.formulation));
}

// ### 2 (cont.) — the summary row's four figures, over the rows given.
//
// Aggregates, not row values: they describe the whole filtered set, so the
// server computes them in the same pass that produces the rows rather than
// having the client re-derive them from whatever subset it happens to hold.
//
// `averageWastePct` is infusion-WEIGHTED — a formulation given twice must not
// weigh as heavily as one given ten thousand times. Rows with no waste figure
// are left out of both halves of the ratio rather than counted as zero, which
// would understate it. Returned as a number; the client formats it.
export function buildFormulationSummary(rows = []) {
  // null/undefined/'' first: Number() turns all three into 0, so a bare
  // Number.isFinite check would read an ABSENT figure as a measured zero — the
  // same trap utils/formulationSummary.js guards against.
  const numeric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  let weightedWaste = 0;
  let weightedInfusions = 0;
  let totalInfusions = 0;

  rows.forEach((row) => {
    const infusions = numeric(row.infusions) || 0;
    totalInfusions += infusions;

    const waste = numeric(row.wasteWeightedPct);
    if (waste === null) return;
    weightedWaste += waste * infusions;
    weightedInfusions += infusions;
  });

  const rates = (key) => rows.map((row) => numeric(row[key])).filter((rate) => rate !== null);
  const minRates = rates('minRate');
  const maxRates = rates('maxRate');

  return {
    totalInfusions,
    // null, not 0, when nothing carries a waste figure: the client renders that
    // as N/A rather than as a confident 0.0%.
    averageWastePct: weightedInfusions > 0 ? weightedWaste / weightedInfusions : null,
    minFlowRate: minRates.length ? Math.min(...minRates) : null,
    maxFlowRate: maxRates.length ? Math.max(...maxRates) : null,
  };
}

// ### 3 — GET /investigation/usage-trends. Fills whatever window the caller
// passes rather than deciding it. A deterministic ~1-in-8 month is omitted on
// purpose: the contract allows absent (month, formulation) pairs, and that's the
// case the chart has to tolerate.
//
// The min-infusions floor decides WHICH FORMULATIONS appear, over the whole
// window rather than per month: a formulation is either in use for the period
// or it isn't. Applying it per month would punch holes in a series whose window
// total clears the floor easily, and those gaps would be indistinguishable from
// the genuinely-absent months above.
//
// Membership comes from `formulationsInUse` — the same decision the table makes
// — not from a second comparison against these monthly numbers. The monthly
// values here and the table's `infusions` are independent series, so comparing
// one floor to both ranked the formulations differently and could leave the two
// panels showing disjoint sets.
export function buildUsageTrendRows(scope = {}) {
  const { startDate, endDate } = scope;
  if (!startDate || !endDate) return [];

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const inUse = formulationsInUse(scope);
  const formulations = formulationsFor(scope).filter((entry) => inUse.has(entry.formulation));
  const rows = [];

  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;

    formulations.forEach((entry) => {
      const seed = hash(`${seedFor('trends', scope)}:${entry.formulation}:${year}-${month}`);
      if (seed % 8 === 0) return;
      rows.push({ year, month, formulation: entry.formulation, infusions: 5 + (seed % 400) });
    });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return rows;
}

// ### 4 — GET /medications/{name}/ashp-concentration. Real ASHP Standardize 4
// Safety reference data (48 drugs), not hashed like the datasets above — see
// data/README.md. Case-insensitive lookup by drug name; returns null for any
// medication ASHP hasn't standardized, which is the badges' N/A case, not an
// error.
export function buildAshpConcentration(medicationName) {
  const name = String(medicationName || '')
    .toLowerCase()
    .trim();
  if (!name) return null;

  const drug = ashpS4sConcentrations.find((entry) => entry.drug_name.toLowerCase() === name);
  if (!drug) return null;

  return {
    medicationName: name,
    concentrations: drug.concentrations.map((conc) => ({
      concentration: conc.concentration,
      unit: conc.unit,
      // Independent flags, not an enum: a row can be standardized for both
      // cohorts and legitimately appear in both Resources tables.
      adult: !!conc.adult,
      pediatric: !!conc.pediatric,
      // Free text in the source data ("Yes, comes in a kit with diluent"), so
      // rendered as-is rather than coerced to a strict Yes/No.
      commerciallyAvailable: conc.commercially_available ?? null,
      // Present on 81 of 97 rows in the source data; absent renders as N/A.
      concentrationVsUnitMismatch: conc.concentration_vs_unit_mismatch ?? null,
    })),
    dosingUnits: drug.dosing_units,
    references: (drug.references || []).map((ref) => ({ text: ref.text, url: ref.url })),
  };
}

// ### 5 — GET /medications/{name}/rtu-formulations. Real RTU catalog data (9
// medications, commercial + 503B), not hashed — see data/README.md.
// Case-insensitive lookup; an empty list means no RTU product exists for this
// drug, the RTU badge's N/A case, not an error.
export function buildRtuFormulations(medicationName) {
  const name = String(medicationName || '')
    .toLowerCase()
    .trim();
  if (!name) return [];

  const medication = rtuFormulationsData.find((entry) => entry.medication_name.toLowerCase() === name);
  if (!medication) return [];

  return medication.rtu_formulations.map((rtu) => ({
    concentrationValue: rtu.concentration,
    concentrationUnit: rtu.concentration_unit,
    volumeValue: rtu.infusion_volume,
    volumeUnit: rtu.infusion_unit,
    diluent: rtu.diluent,
    containerType: rtu.container_type,
    // A missing category predates 503B support and was always commercial; see
    // data/README.md. Every row in the current data has it populated.
    category: rtu.category || 'commercial',
    // For 503b rows this is the Compounder, not the Manufacturer — see
    // RtuAvailabilityPanel, which derives the column label from `category`.
    manufacturer: rtu.manufacturer,
    ndc: rtu.ndc,
  }));
}

export default {
  buildFormulationRows,
  buildFormulationSummary,
  buildUsageTrendRows,
  buildAshpConcentration,
  buildRtuFormulations,
};
